/**
 * Crew Data Utilities
 *
 * Transforms Supabase crew data into the format expected by frontend components.
 * Handles data formatting, calculations, and defaults.
 */

/**
 * Calculate total posts for a crew (linkups + ticket bids)
 * @param {Object} crew - Supabase crew object
 * @returns {number} Total number of posts
 */
export const calculateCrewPosts = crew => {
  const linkups = getCrewMetricCount(crew, [
    'linkUps_nums',
    'linkUpsCount',
    'linkUps',
    'linkupCount',
  ]);
  const bids = getCrewMetricCount(crew, [
    'ticket_bids_num',
    'ticketBidsCount',
    'ticketBids',
    'bidCount',
  ]);
  return linkups + bids;
};

const getCrewMetricCount = (crew, keys) => {
  const rawValue = keys.reduce((value, key) => {
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return crew?.[key];
  }, null);

  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

/**
 * Format member count into human-readable string (e.g., 15200 -> "15.2K")
 * @param {number} count - Raw member count
 * @returns {string} Formatted count
 */
export const formatMemberCount = count => {
  if (!count || count === 0) return '0';

  if (count >= 1000000) {
    return `${Number((count / 1000000).toFixed(1))}M`;
  }

  if (count >= 1000) {
    return `${Number((count / 1000).toFixed(1))}K`;
  }

  return count.toString();
};

/**
 * Format status string for crew card
 * @param {Object} crew - Supabase crew object
 * @param {boolean} includeCategory - Whether to include category in status
 * @param {boolean} alwaysShowPosts - Whether to show posts even when 0
 * @returns {string} Formatted status
 */
export const formatCrewStatus = (crew, includeCategory = true, alwaysShowPosts = true) => {
  const posts = calculateCrewPosts(crew);
  const category = crew.crew_category || 'General';

  if (includeCategory) {
    // Always show posts if alwaysShowPosts is true, or if posts > 0
    if (alwaysShowPosts || posts > 0) {
      const postText = posts === 1 ? 'new post' : 'new posts';
      return `${category} • ${posts} ${postText}`;
    }
    return category;
  }

  return posts > 0 ? `${posts} new posts` : 'No recent activity';
};

/**
 * Transform Supabase crew data to frontend CrewCard format
 * @param {Object} supabaseCrew - Raw crew data from Supabase
 * @param {Object} options - Transformation options
 * @returns {Object} Transformed crew data for frontend components
 */
export const transformCrewData = (supabaseCrew, options = {}) => {
  if (!supabaseCrew) return null;

  const { includeFullDetails = false, defaultImage = null } = options;

  const posts = calculateCrewPosts(supabaseCrew);
  const linkUpsCount = getCrewMetricCount(supabaseCrew, [
    'linkUps_nums',
    'linkUpsCount',
    'linkUps',
    'linkupCount',
  ]);
  const ticketBidsCount = getCrewMetricCount(supabaseCrew, [
    'ticket_bids_num',
    'ticketBidsCount',
    'ticketBids',
    'bidCount',
  ]);
  const formattedMembers = formatMemberCount(supabaseCrew.num_members);

  // Base crew data (used by all components)
  const baseData = {
    id: supabaseCrew.id,
    name: supabaseCrew.crew_name,
    crew_name: supabaseCrew.crew_name, // Include both for compatibility
    category: supabaseCrew.crew_category || 'General',
    image: supabaseCrew.crew_header_image || defaultImage,
    status: formatCrewStatus(supabaseCrew, false, false),
    description: supabaseCrew.crew_description || '', // Always include description

    // Ticketmaster integration (for event fetching)
    attraction_name: supabaseCrew.attraction_name || null,
    attraction_id: supabaseCrew.attraction_id || null,
    venue_id: supabaseCrew.venue_id || null,

    // Different components use different property names for the same data
    // Include both for compatibility
    fans: formattedMembers, // Used by CrewsPage/CrewCard
    followers: formattedMembers, // Used by FindCrewsPage/CrewCard
    memberCount: formattedMembers, // Used by modal

    // Activity metrics (always include for modal)
    linkUpsCount,
    ticketBidsCount,
    linkUps: linkUpsCount.toString(),
    ticketBids: ticketBidsCount.toString(),
    tickets: ticketBidsCount.toString(),
    newPosts: posts.toString(),

    // For backwards compatibility
    artist: supabaseCrew.crew_category,
    location: supabaseCrew.venue_id ? 'TBD' : 'Global',
  };

  // Include full details if requested (for crew detail pages)
  if (includeFullDetails) {
    return {
      ...baseData,
      postCount: posts.toString(),
      ticketListings: '0', // TODO: Get from actual listings data
      hasLinkUps: linkUpsCount > 0,
      createdBy: supabaseCrew.created_by,
      createdAt: supabaseCrew.created_at,
      bannerImage: supabaseCrew.crew_header_image || defaultImage, // For CrewPage hero
    };
  }

  return baseData;
};

/**
 * Transform an array of Supabase crews to frontend format
 * @param {Array} supabaseCrews - Array of raw crew data from Supabase
 * @param {Object} options - Transformation options
 * @returns {Array} Array of transformed crew data
 */
export const transformCrewList = (supabaseCrews, options = {}) => {
  if (!Array.isArray(supabaseCrews)) return [];

  return supabaseCrews.map(crew => transformCrewData(crew, options)).filter(crew => crew !== null);
};

/**
 * Sort crews by various criteria
 * @param {Array} crews - Array of crew objects
 * @param {string} sortBy - Sort criterion ('members', 'posts', 'recent', 'name')
 * @returns {Array} Sorted array of crews
 */
export const sortCrews = (crews, sortBy = 'members') => {
  if (!Array.isArray(crews)) return [];

  const sortedCrews = [...crews];

  switch (sortBy) {
    case 'members':
      return sortedCrews.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));

    case 'posts':
      return sortedCrews.sort((a, b) => (b.postCount || 0) - (a.postCount || 0));

    case 'recent':
      return sortedCrews.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

    case 'name':
      return sortedCrews.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

    default:
      return sortedCrews;
  }
};

/**
 * Filter crews by category
 * @param {Array} crews - Array of crew objects
 * @param {string} category - Category to filter by (empty string = all)
 * @returns {Array} Filtered array of crews
 */
export const filterCrewsByCategory = (crews, category) => {
  if (!Array.isArray(crews) || !category) return crews;

  return crews.filter(
    crew => crew.category && crew.category.toLowerCase() === category.toLowerCase()
  );
};

/**
 * Search crews by name or description
 * @param {Array} crews - Array of crew objects
 * @param {string} query - Search query
 * @returns {Array} Filtered array of crews
 */
export const searchCrews = (crews, query) => {
  if (!Array.isArray(crews) || !query || query.trim() === '') return crews;

  const lowerQuery = query.toLowerCase();

  return crews.filter(crew => {
    const name = (crew.name || '').toLowerCase();
    const description = (crew.description || '').toLowerCase();
    const category = (crew.category || '').toLowerCase();

    return (
      name.includes(lowerQuery) || description.includes(lowerQuery) || category.includes(lowerQuery)
    );
  });
};
